import { Observable, of } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';

import { PayeeVerification, PayeeVerificationStatus, VopMode, VopOutcome, VopOverride, VopResult } from 'src/app/api/models';

/**
 * Where a proposed payout's Verification of Payee (VoP) stands in the
 * payout widget. `POST /payee-verifications` answers it for an IBAN and
 * name pair (apps/cctool-go/docs/outgoing-payments/verification-of-payee.md,
 * "Payouts"); the terms are in docs/contexts/payments/GLOSSARY.md.
 *
 * - `idle`: no pair asked about, or VoP is off.
 * - `waiting`: the server is waiting for the bank's check, in the VoP mode
 *   `GET /payee-verifications/mode` named.
 * - `in_progress`: the check takes longer; try again later.
 * - `result`: the result a payout would be made on.
 * - `error`: the VoP mode could not be read, or the question failed.
 */
export type PayeeVerificationState =
  | { kind: 'idle' }
  | { kind: 'waiting'; mode: VopMode }
  | { kind: 'in_progress'; mode: VopMode }
  | { kind: 'result'; mode: VopMode; result: VopResult }
  | { kind: 'error' };

export const VOP_IDLE: PayeeVerificationState = { kind: 'idle' };

/**
 * The state an answer of `POST /payee-verifications` puts the pair in. An
 * answer that VoP is off, as when the mode changed since it was read, leaves
 * nothing to show.
 */
export function payeeVerificationState(answer: PayeeVerification): PayeeVerificationState {
  switch (answer.status) {
    case PayeeVerificationStatus.Result:
      return answer.result ? { kind: 'result', mode: answer.mode, result: answer.result } : { kind: 'error' };
    case PayeeVerificationStatus.InProgress:
      return { kind: 'in_progress', mode: answer.mode };
    default:
      return VOP_IDLE;
  }
}

/**
 * Asks about the pair once the VoP mode is known (issue #682): in `off`
 * nothing is asked and the pair stays `idle`; otherwise `waiting` at once,
 * in the mode, then the answer's state. A failed mode read or question is
 * `error`.
 */
export function verifyPayee$(
  mode$: Observable<VopMode>,
  ask: () => Observable<PayeeVerification>,
): Observable<PayeeVerificationState> {
  return mode$.pipe(
    switchMap((mode) => mode === VopMode.Off
      ? of(VOP_IDLE)
      : ask().pipe(
        map(payeeVerificationState),
        startWith<PayeeVerificationState>({ kind: 'waiting', mode }),
      )),
    catchError(() => of<PayeeVerificationState>({ kind: 'error' })),
  );
}

/**
 * True when the result needs a VoP override for a payout: `enforce`, and not
 * a match. A rejected request is overridden too: a rejection can be ours,
 * and must not stop a payout that has to go out.
 */
export function vopOverrideOffered(state: PayeeVerificationState): boolean {
  return state.kind === 'result' && state.mode === VopMode.Enforce && state.result.outcome !== VopOutcome.Match;
}

/** True when the override needs a reason: the result is a no match or a rejected request. */
export function vopOverrideNeedsReason(state: PayeeVerificationState): boolean {
  return (
    vopOverrideOffered(state) &&
    state.kind === 'result' &&
    (state.result.outcome === VopOutcome.NoMatch || state.result.outcome === VopOutcome.Rejected)
  );
}

/** The handler's override, as the payout widget holds it. */
export interface VopOverrideChoice {
  made: boolean;
  reason: string | null;
}

/**
 * True only in `enforce`, while the server would refuse the payout for VoP:
 * waiting for the answer, in progress, or a non-match (a rejected request
 * included) without a complete override. In `observe` VoP never holds a
 * payout. A pair not asked about (`idle`, VoP off included) or whose
 * question failed (`error`) is left to the server, which refuses it in
 * `enforce` only.
 */
export function vopBlocksPayout(state: PayeeVerificationState, choice: VopOverrideChoice): boolean {
  switch (state.kind) {
    case 'waiting':
    case 'in_progress':
      return state.mode === VopMode.Enforce;
    case 'result':
      if (state.mode !== VopMode.Enforce) {
        return false;
      }
      if (!vopOverrideOffered(state)) {
        return false;
      }
      return !choice.made || (vopOverrideNeedsReason(state) && !(choice.reason ?? '').trim());
    default:
      return false;
  }
}

/** The `vop_override` to send with the payout, or undefined for none. */
export function vopOverrideRequest(state: PayeeVerificationState, choice: VopOverrideChoice): VopOverride | undefined {
  if (!vopOverrideOffered(state) || !choice.made) {
    return undefined;
  }
  const reason = (choice.reason ?? '').trim();
  return reason ? { reason } : {};
}
